/**
 * Form Validation Component
 *
 * Provides client-side and server-side validation for HTML forms with automatic submit
 * button management, validation message display, and optional change tracking.
 *
 * ## Attributes on Component Element (form)
 * - `data-component="formValidation"` - Initialize component (required)
 * - `data-server-validation="/url"` - Server validation endpoint (optional)
 * - `data-track-changes` - Disable submit if form unchanged (optional)
 * - `data-hide-validation-identifiers="id1,id2"` - Hide specific validation messages
 *
 * ## Elements Inside Component
 * - `data-validation-place="fieldName"` - Field-specific validation message container
 * - `data-validation-person-id="123"` - Person-specific message container
 * - `data-validation-enable-when="key"` - Enable/disable element based on server response (input/button)
 * - `data-validation-replace="key"` - Replace text content from server response
 * - `data-force-disabled` - Manually force submit button disabled
 *
 * ## Elements Outside Component
 * - `data-validation-place-other` - General validation message container (can be outside form)
 * - `data-validation-place-other="formId"` - Link external container to specific form
 *
 * ## Submit Button Enabled When
 * 1. HTML5 validation passes
 * 2. No server-side errors (severity: 'error')
 * 3. Form has changes (if data-track-changes enabled)
 * 4. Not manually disabled (data-force-disabled)
 *
 * ## Notes
 * - Validates on input/change with 20ms debounce
 * - Shows loading indicator on submit
 * - Works with external submit buttons (form="formId")
 * - Defers validation until dialog opens if form is inside a dialog
 */

import { replaceIcons } from '../icons';

type PersonId = number;

interface ValidationIssue {
  severity: 'information' | 'non_competing_reason' | 'error';
  field?: string;
  message: string;
  description: string;
  identifier?: string;
  related_to_person_ids?: {
    person_ids: PersonId[];
    message: string;
  };
}

interface ValidationIssueCollection {
  issues: ValidationIssue[];
  extra: Record<string, unknown>;
}

export default (element: HTMLElement) => {
  // This component validates forms and enables/disables submit buttons based on validity

  if (!(element instanceof HTMLFormElement)) {
    console.error(
      'formValidation component must be attached to a form element',
    );
    return;
  }

  const form = element;
  let submitButtons = Array.from(
    form.querySelectorAll<HTMLButtonElement>('button[type="submit"]'),
  );

  if (form.id) {
    const formAttributeButtons = document.querySelectorAll<HTMLButtonElement>(
      `button[form="${form.id}"]`,
    );
    submitButtons = Array.from(submitButtons).concat(
      Array.from(formAttributeButtons),
    );
  }

  if (submitButtons.length === 0) {
    console.error('No submit button found in form');
    return;
  }

  const hiddenIdentifiers =
    form
      .getAttribute('data-hide-validation-identifiers')
      ?.split(',')
      .map((id) => id.trim()) ?? [];

  // Track server-side validation errors
  let hasServerErrors = false;

  // Track changes if enabled
  const trackChanges = form.hasAttribute('data-track-changes');
  let initialFormState: string | null = null;

  // Capture initial form state if tracking is enabled
  if (trackChanges) {
    const formData = new FormData(form);
    // biome-ignore lint/suspicious/noExplicitAny: FormData is valid input for URLSearchParams constructor
    initialFormState = new URLSearchParams(formData as any).toString();
  }

  // Function to check if form has changes
  const hasChanges = (): boolean => {
    if (!trackChanges) {
      return true; // Always return true if not tracking changes
    }

    const currentFormData = new FormData(form);
    const currentFormState = new URLSearchParams(
      // biome-ignore lint/suspicious/noExplicitAny: FormData is valid input for URLSearchParams constructor
      currentFormData as any,
    ).toString();

    return currentFormState !== initialFormState;
  };

  const validationPlaceOther =
    form.querySelector('[data-validation-place-other]') ??
    document.querySelector(`[data-validation-place-other="${form.id}"]`);

  // Function to clear all validation messages
  const clearValidationMessages = () => {
    const validationPlaces = form.querySelectorAll(
      '[data-validation-place], [data-validation-person-id]',
    );
    validationPlaces.forEach((place) => {
      place.innerHTML = '';
    });
    if (validationPlaceOther) {
      validationPlaceOther.innerHTML = '';
    }
  };

  // Function to render a validation issue
  const renderValidationIssue = (issue: ValidationIssue) => {
    // Skip rendering if identifier is in hidden list
    if (issue.identifier && hiddenIdentifiers.includes(issue.identifier)) {
      return;
    }

    // Find the target element for this validation
    let targetElement: Element | null = null;

    if (issue.field) {
      targetElement = form.querySelector(
        `[data-validation-place="${issue.field}"]`,
      );
    }

    if (!targetElement) {
      targetElement = validationPlaceOther;
    }

    if (!targetElement) {
      console.warn(
        `No validation place found for field ${issue.field || 'other'}`,
      );
      return;
    }

    // Create the error box
    const errorBox = document.createElement('div');
    errorBox.className = 'errorBox';
    errorBox.setAttribute('role', 'alert');
    errorBox.setAttribute('aria-live', 'polite');

    const icon = document.createElement('i');
    icon.setAttribute('data-lucide', 'circle-alert');
    icon.className = 'errorBox__icon';
    icon.setAttribute('aria-hidden', 'true');

    const content = document.createElement('div');
    content.className = 'errorBox__content';

    const title = document.createElement('h4');
    title.className = 'errorBox__title';
    title.textContent = issue.message;

    const description = document.createElement('p');
    description.className = 'errorBox__description';
    description.textContent = issue.description;

    content.appendChild(title);
    content.appendChild(description);
    errorBox.appendChild(icon);
    errorBox.appendChild(content);

    targetElement.appendChild(errorBox);

    // Re-initialize Lucide icons for the new icon
    replaceIcons(targetElement as HTMLElement);
  };

  // Track validation state
  let isValidating = false;
  let debounceTimer: ReturnType<typeof setTimeout> | null = null;
  let abortController: AbortController | null = null;

  // Function to show loading indicator
  const showLoadingIndicator = () => {
    if (isValidating) return;

    const loadingPlace = form.querySelector('[data-validation-place-other]');
    if (!loadingPlace) return;

    const loadingDiv = document.createElement('div');
    loadingDiv.className = 'validation-loading';
    loadingDiv.setAttribute('data-validation-loading', '');
    loadingDiv.textContent = 'Probíhá kontrola...';

    loadingPlace.appendChild(loadingDiv);
  };

  // Function to hide loading indicator
  const hideLoadingIndicator = () => {
    const loadingDiv = form.querySelector('[data-validation-loading]');
    if (loadingDiv) {
      loadingDiv.remove();
    }
  };

  // Function to perform server-side validation
  const performServerValidation = async (signal: AbortSignal) => {
    // Check if server-side validation is enabled for this form
    const validationUrl = form.getAttribute('data-server-validation');
    if (!validationUrl) {
      return;
    }

    // Prevent concurrent validation requests
    if (isValidating) {
      return;
    }

    isValidating = true;
    showLoadingIndicator();

    // Get form data
    const formData = new FormData(form);
    // biome-ignore lint/suspicious/noExplicitAny: FormData is valid input for URLSearchParams constructor (source: https://developer.mozilla.org/en-US/docs/Web/API/URLSearchParams/URLSearchParams)
    const urlEncodedData = new URLSearchParams(formData as any);

    try {
      const response = await fetch(validationUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: urlEncodedData,
        signal,
      });

      if (!response.ok) {
        // If validation endpoint fails, don't block the form
        console.error('Validation endpoint failed:', response.statusText);
        return;
      }

      const collection: ValidationIssueCollection = await response.json();
      const issues = collection.issues;

      // Clear previous validation messages
      clearValidationMessages();

      // Check if there are any errors (not just warnings)
      hasServerErrors = issues.some((issue) => issue.severity === 'error');

      // Collect all messages per person ID to avoid concatenation issues
      const personMessages = new Map<PersonId, string[]>();
      issues.forEach((issue) => {
        const relatedToPersonIds = issue.related_to_person_ids;
        if (relatedToPersonIds) {
          relatedToPersonIds.person_ids.forEach((personId) => {
            const messages = personMessages.get(personId);
            if (messages) {
              messages.push(relatedToPersonIds.message);
            } else {
              personMessages.set(personId, [relatedToPersonIds.message]);
            }
          });
        }
      });

      // Render person-specific messages
      personMessages.forEach((messages, personId) => {
        const personPlace = form.querySelector(
          `[data-validation-person-id="${personId}"]`,
        );
        if (personPlace) {
          personPlace.textContent = ` • ${messages.join(' • ')}`;
        }
      });

      // Render all validation issues
      issues.forEach(renderValidationIssue);

      // data-validation-enable-when handling
      const enableWhen = form.querySelectorAll<HTMLElement>(
        '[data-validation-enable-when]',
      );
      enableWhen.forEach((el) => {
        const condition = el.getAttribute('data-validation-enable-when');
        if (condition && collection.extra[condition] !== undefined) {
          const value = collection.extra[condition];
          if (typeof value === 'boolean') {
            if (
              el instanceof HTMLInputElement ||
              el instanceof HTMLButtonElement
            ) {
              el.toggleAttribute('data-force-disabled', !value);
            } else {
              console.warn(
                'data-validation-enable-when can only be used on input and button elements',
              );
            }
          } else {
            console.warn(
              'data-validation-enable-when can only be used with boolean values',
            );
          }
        }
      });

      // data-validation-replace handling
      const replaceElements = form.querySelectorAll<HTMLElement>(
        '[data-validation-replace]',
      );
      replaceElements.forEach((el) => {
        const key = el.getAttribute('data-validation-replace');
        if (key && collection.extra[key] !== undefined) {
          const value = collection.extra[key];
          if (typeof value === 'string' || typeof value === 'number') {
            el.textContent = String(value);
          } else {
            console.warn(
              'data-validation-replace can only be used to replace with string or number values',
            );
          }
        }
      });

      // Update button state
      updateButtonState();
    } catch (error) {
      // Don't log errors for aborted requests
      if (error instanceof Error && error.name !== 'AbortError') {
        console.error('Failed to validate form:', error);
      }
    } finally {
      isValidating = false;
      hideLoadingIndicator();
    }
  };

  // Function to trigger debounced server-side validation
  const debouncedServerValidation = (debounce: boolean = true) => {
    // Clear existing timer
    if (debounceTimer !== null) {
      clearTimeout(debounceTimer);
    }

    // Abort any in-flight request
    if (abortController !== null) {
      abortController.abort();
    }

    // Create new abort controller
    abortController = new AbortController();
    const signal = abortController.signal;

    // Set new timer
    debounceTimer = setTimeout(
      () => {
        performServerValidation(signal);
      },
      debounce ? 20 : 0,
    );
  };

  // Function to update button state
  const updateButtonState = () => {
    const isValid = form.checkValidity();

    // Check type="hidden" inputs as well
    const hiddenInputsValid = Array.from(form.elements)
      .filter(
        (el): el is HTMLInputElement =>
          el instanceof HTMLInputElement &&
          el.type === 'hidden' &&
          el.hasAttribute('required'),
      )
      .every((input) => input.value.trim() !== '');

    // Disable button if either HTML5 validation fails OR there are server errors OR no changes (when tracking)
    // Warnings do not block submission
    const formHasChanges = hasChanges();

    submitButtons.forEach((submitButton) => {
      const forceDisabled = submitButton.hasAttribute('data-force-disabled');
      if (
        isValid &&
        hiddenInputsValid &&
        !hasServerErrors &&
        formHasChanges &&
        !forceDisabled
      ) {
        submitButton.disabled = false;
        submitButton.classList.remove('button--disabled');
      } else {
        submitButton.disabled = true;
        submitButton.classList.add('button--disabled');
      }
    });
  };

  Array.from(form.elements).forEach((input) => {
    if (
      input instanceof HTMLInputElement ||
      input instanceof HTMLSelectElement ||
      input instanceof HTMLTextAreaElement
    ) {
      input.addEventListener('input', updateButtonState);
      input.addEventListener('change', () => {
        updateButtonState();
        debouncedServerValidation();
      });
    }
  });

  form.addEventListener('reset', () => {
    // Clear validation messages on reset
    clearValidationMessages();
    hasServerErrors = false;
    updateButtonState();
    if (abortController) {
      abortController.abort();
    }
    if (debounceTimer !== null) {
      clearTimeout(debounceTimer);
    }
  });

  // Handle form submission - disable button and show loading indicator
  form.addEventListener('submit', () => {
    localStorage.setItem('lastFormSubmitTimestamp', Date.now().toString());

    submitButtons.forEach((button) => {
      // Disable the button
      button.disabled = true;
      button.classList.add('button--disabled');

      // Store original content
      const originalContent = button.innerHTML;
      button.setAttribute('data-original-content', originalContent);

      // Create loading indicator
      const loadingIcon = document.createElement('i');
      loadingIcon.setAttribute('data-lucide', 'loader-circle');
      loadingIcon.className = 'button__icon button__icon--loading';
      loadingIcon.setAttribute('aria-hidden', 'true');

      const loadingText = document.createTextNode('Odesílání...');

      // Replace button content with loading indicator
      button.innerHTML = '';
      button.appendChild(loadingIcon);
      button.appendChild(loadingText);

      // Initialize Lucide icon
      replaceIcons(button);
    });
  });

  // Initial state check
  updateButtonState();

  // Do not trigger server validation until dialog is shown
  const parentDialog = element.closest('dialog');
  if (parentDialog instanceof HTMLDialogElement) {
    let wasOpen = parentDialog.open;
    const mutationObserver = new MutationObserver(() => {
      if (parentDialog.open && !wasOpen) {
        debouncedServerValidation(false);
      }
      wasOpen = parentDialog.open;
    });
    mutationObserver.observe(parentDialog, { attributes: true });
    if (parentDialog.open) {
      wasOpen = true;
      debouncedServerValidation(false);
    }
  } else {
    debouncedServerValidation(false);
  }
};
